import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FaBell, FaCheck } from 'react-icons/fa';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationContainer = styled.div`
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ $isRead, theme }) => 
    $isRead ? 'transparent' : 'rgba(0, 123, 255, 0.05)'};
  transition: background-color 0.2s;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ $priority, theme }) => {
    switch ($priority) {
      case 'high':
        return theme.colors.danger;
      case 'medium':
        return theme.colors.warning;
      default:
        return theme.colors.primary;
    }
  }};
  color: white;
  margin-right: 12px;
  flex-shrink: 0;
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  overflow: hidden;
`;

const Title = styled.h4`
  margin: 0 0 4px;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const Message = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 4px;
  }
`;

const Time = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-left: 8px;
  
  @media (max-width: 480px) {
    display: block;
    margin-left: 0;
    margin-top: 4px;
  }
`;

const ReadIndicator = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.success};
  font-size: 12px;
  margin-left: 8px;
`;

const NotificationItem = ({ notification }) => {
  const navigate = useNavigate();
  const { markAsRead, isNotificationRead } = useNotifications();
  
  const isRead = isNotificationRead(notification.id);
  
  const handleClick = () => {
    // Mark as read if not already
    if (!isRead) {
      markAsRead(notification.id);
    }
    
    // Navigate to action URL if provided
    if (notification.actionURL) {
      navigate(notification.actionURL);
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    // Convert Firestore timestamp to JS Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM d, h:mm a');
  };
  
  return (
    <NotificationContainer onClick={handleClick} $isRead={isRead}>
      <IconContainer $priority={notification.priority}>
        <FaBell />
      </IconContainer>
      <ContentContainer>
        <Title>
          {notification.title}
          {isRead && (
            <ReadIndicator>
              <FaCheck style={{ marginRight: '4px' }} />
              Read
            </ReadIndicator>
          )}
        </Title>
        <Message>{notification.message}</Message>
        <Time>{formatDate(notification.createdAt)}</Time>
      </ContentContainer>
    </NotificationContainer>
  );
};

export default NotificationItem; 